import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { payrollReviewGateway } from '../../payroll-review';
import { customPayRunGateway } from '../index';
import { reportsGateway } from '../../reports';
import type { PayrollRegisterResponse } from '../../reports/types';
import { useIdentity } from '../../identity/state/useIdentity';
import { approveBlockedReason, viewerProcessedRun, type RegisterLoadKind } from '../../payroll-review/approvalGuard';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { isForbidden, isNotFound, payrollReviewErrorMessage } from '../../payroll-review/payrollReviewErrors';
import { PayrollRunStatus, type PayrollReviewRun } from '../../payroll-review/types';
import { PayrollRunStatusBadge } from '../../payroll-review/components/PayrollRunStatusBadge';
import { ApproveRunDialog } from '../../payroll-review/components/ApproveRunDialog';
import { RejectRunDialog } from '../../payroll-review/components/RejectRunDialog';
import { ReviewSafetyNotice } from '../../payroll-review/components/ReviewSafetyNotice';
import { CUSTOM_PAY_RUN_REJECTED_NOTE, READ_ONLY_NOTICE } from '../../payroll-review/reviewCopy';
import { formatDate, formatDateTime, formatMoney, formatPeriod } from '../../payroll-review/formatting';
import '../../payroll-review/payroll-review.css';
import type { StepProps } from './CustomPayRunWizard';
import { NotConnected } from './NotConnected';
import { PayrollRecordsTable } from './PayrollRecordsTable';
import { RunSummary } from './RunSummary';
import { WithdrawRunDialog } from './WithdrawRunDialog';
import { DuplicateAcknowledgementsPanel } from './DuplicateAcknowledgementsPanel';
import { ExportCsvButton } from './ExportCsvButton';
import { CPR_PERMISSIONS, NO_PERMISSION_NOTE } from '../permissions';
import { usePermissionCheck } from '../../identity/state/usePermissions';
import { usePayrollRecords } from './usePayrollRecords';

/** Step 3 — Pending Payroll: the run as Payroll Review holds it, with its LIVE review status. "Approve Paystubs" and
 * "Unapprove Paystubs" are Payroll Review's existing run-level approve and reject (same gateway, same pre-checks, same server
 * rules — the person who created or submitted the run cannot approve it, and the server also refuses a user who ran a
 * calculation in it). "Approve Paystubs" is only shown to another user who can approve (never the preparer/submitter).
 * "Unapprove Paystubs" rejects the whole run (final, reason required); the backend then emails the person who processed it.
 * Approving individual paystubs has no backend, so there is no per-paystub approval here. */
export function PendingPayrollStep({ run, approverView, refreshRun, goToStep }: StepProps) {
  const { me } = useIdentity();
  const can = usePermissionCheck();
  // Display only: the server re-checks `payrollruns.approve` on every approve or reject, and its 403 still turns this step read-only.
  const canApproveRun = can(CPR_PERMISSIONS.approveRun);
  const payrollRunId = run.payrollRunId;
  const { loading, error, records } = usePayrollRecords(run.id, true);
  const [search, setSearch] = useState('');

  const [reviewRun, setReviewRun] = useState<PayrollReviewRun | null>(null);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [register, setRegister] = useState<{ kind: RegisterLoadKind; data: PayrollRegisterResponse | null }>({ kind: 'loading', data: null });
  const [dialog, setDialog] = useState<'approve' | 'reject' | null>(null);
  const [busy, setBusy] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [readOnly, setReadOnly] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [withdrawError, setWithdrawError] = useState<string | null>(null);

  const loadReview = useCallback(
    async (signal?: AbortSignal) => {
      if (!payrollRunId) return;
      setReviewError(null);
      try {
        const fresh = await payrollReviewGateway.getRun(payrollRunId, signal);
        if (!signal?.aborted) setReviewRun(fresh);
      } catch (err) {
        if (signal?.aborted) return;
        if (isForbidden(err)) setReadOnly(true);
        else setReviewError(payrollReviewErrorMessage(err, 'Could not load this payroll run from Payroll Review.'));
      }
    },
    [payrollRunId],
  );

  useEffect(() => {
    const controller = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadReview(controller.signal);
    return () => controller.abort();
  }, [loadReview]);

  useEffect(() => {
    if (!payrollRunId) return;
    const controller = new AbortController();
    reportsGateway
      .getPayrollRegister(payrollRunId, controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) setRegister({ kind: 'ready', data });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setRegister({ kind: isForbidden(err) ? 'forbidden' : isNotFound(err) ? 'notFound' : 'error', data: null });
      });
    return () => controller.abort();
  }, [payrollRunId]);

  const status = run.payrollRunStatus;
  const isPending = status === PayrollRunStatus.PendingApproval;
  const blockedReason =
    reviewRun === null
      ? 'Approve becomes available once the run has loaded from Payroll Review.'
      : approveBlockedReason({
          viewerUserId: me?.userId,
          run: reviewRun,
          registerKind: register.kind,
          registerLines: register.data?.lines.length ?? 0,
        });
  const canApprove = isPending && !readOnly && !busy && blockedReason === null;
  // "Approve Paystubs" is shown only to another user who can approve: never to the run's creator or submitter. The submitter is
  // read from the live review run here, so this holds even before the wizard's own read has settled. Server rules still apply.
  // EPN-241: the run's own preparer (creator or submitter) can neither approve NOR reject it; they can WITHDRAW it while it is pending. Display rules only: the
  // server enforces every one of them.
  const isPreparer = reviewRun
    ? viewerProcessedRun({ viewerUserId: me?.userId, createdByUserId: reviewRun.createdByUserId, submittedByUserId: reviewRun.submittedByUserId })
    : !approverView;
  const canWithdraw = isPending && isPreparer && can(CPR_PERMISSIONS.write) && !readOnly && !busy;
  const showApprove =
    isPending &&
    approverView &&
    canApproveRun &&
    !(reviewRun && viewerProcessedRun({ viewerUserId: me?.userId, createdByUserId: reviewRun.createdByUserId, submittedByUserId: reviewRun.submittedByUserId }));
  const showReject = isPending && canApproveRun && !isPreparer;
  const canReject = isPending && !readOnly && !busy && reviewRun !== null;

  async function decide(decision: 'approve' | 'reject', reason?: string) {
    if (!payrollRunId) return;
    setBusy(true);
    setDialogError(null);
    try {
      if (decision === 'approve') await payrollReviewGateway.approveRun(payrollRunId);
      else await payrollReviewGateway.rejectRun(payrollRunId, reason ?? '');
      setDialog(null);
      await refreshRun();
      await loadReview();
      if (decision === 'approve') goToStep('approved-payroll');
    } catch (err) {
      if (isForbidden(err)) {
        setReadOnly(true);
        setDialog(null);
      } else {
        // Someone else may already have acted; re-read so the page shows what the server holds.
        setDialogError(payrollReviewErrorMessage(err, decision === 'approve' ? 'Could not approve this payroll run.' : 'Could not reject this payroll run.'));
        await refreshRun();
        await loadReview();
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleWithdraw(reason: string) {
    setBusy(true);
    setWithdrawError(null);
    try {
      await customPayRunGateway.withdraw(run.id, reason);
      setWithdrawOpen(false);
      await refreshRun();
      await loadReview();
    } catch (err) {
      // Someone may have approved it in the meantime; re-read so the page shows what the server holds.
      setWithdrawError(friendlyErrorMessage(err, 'Could not withdraw this run.'));
      await refreshRun();
      await loadReview();
    } finally {
      setBusy(false);
    }
  }

  async function handleRefresh() {
    setRefreshing(true);
    try {
      await refreshRun();
      await loadReview();
    } finally {
      setRefreshing(false);
    }
  }

  const period = formatPeriod(run.periodStartDate, run.periodEndDate);

  return (
    <div>
      <RunSummary run={run}>
        <div className="cpr-summary__tools">
          <input
            className="search-box"
            type="search"
            placeholder="Search in Table"
            aria-label="Search in table"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <ExportCsvButton runId={run.id} />
        </div>
      </RunSummary>

      <div className="cpr-notice" role="status">
        <p>
          This run is in Payroll Review{payrollRunId ? <> as <code>{payrollRunId}</code></> : null}. Review status: <PayrollRunStatusBadge status={status} />
          {reviewRun?.submittedByEmailSnapshot && (
            <>
              {' '}
              · Submitted by {reviewRun.submittedByEmailSnapshot}, {formatDateTime(reviewRun.submittedForApprovalAtUtc)}
            </>
          )}
        </p>
        {status === PayrollRunStatus.Rejected && <p>{CUSTOM_PAY_RUN_REJECTED_NOTE}</p>}
        {status === PayrollRunStatus.Rejected && reviewRun?.rejectionReason && <p>Reason: {reviewRun.rejectionReason}</p>}
        {payrollRunId && (
          <p>
            <Link to={`/payroll-review/${payrollRunId}`}>Review in Payroll Review</Link>{' '}
            <button type="button" className="link" disabled={refreshing} onClick={() => void handleRefresh()}>
              {refreshing ? 'Refreshing…' : 'Refresh review status'}
            </button>
          </p>
        )}
      </div>
      <ReviewSafetyNotice />
      <DuplicateAcknowledgementsPanel runId={run.id} />

      {reviewError && (
        <p className="cpr-notice cpr-notice--error" role="alert">
          {reviewError}
        </p>
      )}
      {loading && <p className="muted">Loading…</p>}
      {error && (
        <p className="cpr-notice cpr-notice--error" role="alert">
          {error}
        </p>
      )}
      {!loading && !error && (
        <PayrollRecordsTable records={records} search={search} showSelection emptyMessage="No payroll records for this run." />
      )}

      {readOnly && (
        <p className="muted" role="status">
          {READ_ONLY_NOTICE}
        </p>
      )}
      {isPending && !readOnly && blockedReason && (
        <p id="cpr-approve-help" className="muted">
          {blockedReason}
        </p>
      )}
      {isPending && isPreparer && (
        <p className="muted" role="status">
          You prepared or sent this run, so you can&apos;t approve or reject it. You can withdraw it while it is waiting for approval.
        </p>
      )}
      {status === PayrollRunStatus.Withdrawn && (
        <p className="cpr-notice cpr-notice--warning" role="status" data-testid="withdrawn-notice">
          <strong>Withdrawn by the preparer.</strong> {reviewRun?.rejectedByEmailSnapshot ?? 'The preparer'} took this run back before it was approved
          {reviewRun?.rejectedAtUtc ? `, ${formatDateTime(reviewRun.rejectedAtUtc)}` : ''}.
          {reviewRun?.rejectionReason ? ` Reason: “${reviewRun.rejectionReason}”` : ''} It is final and nothing was paid: start a new Custom Pay Run to try again.
        </p>
      )}
      {isPending && !canApproveRun && !isPreparer && (
        <p className="muted" role="status">
          You can review this run, but approving or rejecting it needs the approve permission. {NO_PERMISSION_NOTE}
        </p>
      )}
      {(status === PayrollRunStatus.Approved || status === PayrollRunStatus.Rejected || status === PayrollRunStatus.Withdrawn) && (
        <p className="muted" role="status">
          This run is {status === PayrollRunStatus.Approved ? 'approved' : status === PayrollRunStatus.Withdrawn ? 'withdrawn' : 'rejected'}, so there is nothing left to approve or unapprove here.
        </p>
      )}
      <p className="muted">
        Approval applies to the whole run. Approving or unapproving individual paystubs is not yet connected. <NotConnected />
      </p>

      <div className="cpr-footer">
        <button type="button" className="cpr-btn cpr-btn--outline" onClick={() => goToStep('custom-payroll')}>
          Back to Custom Payroll
        </button>
        <div className="cpr-footer__group">
          {/* A run that was sent for approval cannot be discarded. Its own preparer WITHDRAWS it while it is pending (EPN-241); a reviewer rejects it. */}
          {canWithdraw && (
            <button
              type="button"
              className="cpr-btn cpr-btn--outline"
              title="Take this run back before it is approved. You enter a reason; it stays in the history as Withdrawn."
              onClick={() => {
                setWithdrawError(null);
                setWithdrawOpen(true);
              }}
            >
              Withdraw run
            </button>
          )}
          {showReject && (
            <button
              type="button"
              className="cpr-btn cpr-btn--dark"
              disabled={!canReject}
              title="Rejects the whole run in Payroll Review. A rejected run is final."
              onClick={() => {
                setDialogError(null);
                setDialog('reject');
              }}
            >
              Unapprove Paystubs
            </button>
          )}
          {showApprove && (
            <button
              type="button"
              className="cpr-btn cpr-btn--green"
              disabled={!canApprove}
              aria-describedby={blockedReason && isPending ? 'cpr-approve-help' : undefined}
              onClick={() => {
                setDialogError(null);
                setDialog('approve');
              }}
            >
              Approve Paystubs
            </button>
          )}
          {status === PayrollRunStatus.Approved && (
            <button type="button" className="cpr-btn" onClick={() => goToStep('approved-payroll')}>
              Go to Approved Payroll
            </button>
          )}
        </div>
      </div>

      {dialog === 'approve' && (
        <ApproveRunDialog
          periodLabel={period}
          checkDateLabel={formatDate(run.checkDate)}
          netPayLabel={register.data ? formatMoney(register.data.totals.netPay) : ''}
          busy={busy}
          error={dialogError}
          onConfirm={() => void decide('approve')}
          onCancel={() => setDialog(null)}
        />
      )}
      {withdrawOpen && <WithdrawRunDialog busy={busy} error={withdrawError} onConfirm={(reason) => void handleWithdraw(reason)} onCancel={() => setWithdrawOpen(false)} />}
      {dialog === 'reject' && (
        <RejectRunDialog periodLabel={period} busy={busy} error={dialogError} onConfirm={(reason) => void decide('reject', reason)} onCancel={() => setDialog(null)} />
      )}
    </div>
  );
}
